import { useQuery } from "@apollo/client"
import { format } from "date-fns"
import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import invariant from "tiny-invariant"
import { getFragmentData } from "~/__generated__"
import {
  InfluencerSortFieldEnum,
  InfluencerTableFragment,
} from "~/__generated__/graphql"
import { useHeaderContext } from "~/common/HeaderContext"
import { formatCentsAsCurrency } from "~/common/format-currency"
import { castingCallsPath } from "~/common/paths"
import { BackToContext, useGetBackTo } from "~/common/useBackNavigation"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { InfluencersTable } from "~/screens/rosters/my-roster/components/InfluencersTable"
import { TableCell, TableHead } from "~/shadcn/ui/table"
import { SortableColumn } from "../rosters/components/SortableColumn"
import { InfluencerActionsProvider } from "../rosters/my-roster/components/InfluencerActionsContext"
import { useCastingCallInfluencers } from "../rosters/useInfluencers"
import { FULL_CASTING_CALL_FRAGMENT, VIEW_CASTING_CALL_QUERY } from "./data/api"

export const ViewCastingCallSubmissionsScreen = () => {
  const { id: castingCallId } = useParams()
  const { setHeaderText, setMode, setBackTo } = useHeaderContext()
  invariant(castingCallId, "expected id param")

  const { data, loading } = useQuery(VIEW_CASTING_CALL_QUERY, {
    variables: {
      castingCallId,
    },
  })
  const castingCall = getFragmentData(
    FULL_CASTING_CALL_FRAGMENT,
    data?.castingCall
  )

  const backTo = useGetBackTo(BackToContext.CastingCall, castingCallsPath({}))
  useEffect(() => {
    setHeaderText(castingCall?.campaignName || "")
    setMode("back")
    setBackTo(backTo)
    return () => {
      setHeaderText("")
      setMode("title")
      setBackTo(undefined)
    }
  }, [setHeaderText, setMode, castingCall, setBackTo, backTo])

  useDocumentTitle(
    `Submissions · ${castingCall?.campaignName || "Casting Call"}`,
    { ready: !loading }
  )

  const castingCallInfluencers = useCastingCallInfluencers(castingCallId)

  const submissionCount =
    castingCallInfluencers.data?.castingCall?.influencers?.totalCount
  useTrackScreenEvent(
    "view_casting_call_submissions",
    useMemo(
      () =>
        submissionCount === undefined
          ? null
          : { castingCallId, submission_count: submissionCount },
      [castingCallId, submissionCount]
    )
  )

  if (
    !castingCallInfluencers.loading &&
    castingCallInfluencers.data?.castingCall?.influencers?.edges.length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        No Influencers to Display
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col pb-4 relative py-4 min-h-[420px]">
      <InfluencerActionsProvider source="casting_call">
        <InfluencersTable
          useInfluencersResult={castingCallInfluencers}
          source="casting_call"
          hideCheckboxes
          disableActions
          hideCreatorType
          hideNewInfluencerButton
          className="left-0 right-0"
          rightColumnHeadings={
            <>
              <TableHead sticky>
                <SortableColumn field={InfluencerSortFieldEnum.AgencyName}>
                  Agency
                </SortableColumn>
              </TableHead>
              <TableHead sticky>Amount</TableHead>
              <TableHead sticky>Notes</TableHead>
              <TableHead sticky>Submitted</TableHead>
            </>
          }
          rightColumnSlot={(influencer: InfluencerTableFragment) => {
            const edge =
              castingCallInfluencers?.data?.castingCall?.influencers?.edges.find(
                (x) => x.node.id === influencer.id
              )

            return (
              <>
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span>{influencer.agency?.name}</span>
                  </div>
                </TableCell>

                <TableCell className="whitespace-nowrap">
                  {edge?.totalRateCents
                    ? formatCentsAsCurrency(edge.totalRateCents)
                    : null}
                </TableCell>
                <TableCell className="max-w-[200px]">
                  {edge?.talentAgencyNotes}
                </TableCell>
                <TableCell>
                  {edge ? format(edge?.createdAt, "MMM d") : ""}
                </TableCell>
              </>
            )
          }}
        />
      </InfluencerActionsProvider>
    </div>
  )
}
